import { EffectComposer, Bloom } from "@react-three/postprocessing"
import { useFrame } from "@react-three/fiber"
import { useMemo, useRef, useEffect } from "react"
import { useIsMobile } from "./utils/useIsMobile"
import { createTimeline, easeInOut, easeOut } from "./EffectTimeline"

export default function EffectOrchestreur({ progress, children }) {
    const isMobile = useIsMobile()
    const bloomRef = useRef(null)

    useEffect(() => {
        if (!bloomRef.current) return
        const effect = bloomRef.current
        Object.defineProperty(effect, 'luminanceSmoothing', {
            get() { return effect.luminanceMaterial?.smoothing ?? 0 },
            set(v) { if (effect.luminanceMaterial) effect.luminanceMaterial.smoothing = v },
        })
    }, [])

    const tl = useMemo(() => {
        const maxBloom = isMobile ? 100 : 4000

        return {
            // Éblouissement qui monte en fin d'acte 2,
            // traverse la transition 2→3, redescend progressivement dans acte 3
            intensity: createTimeline([
                // Acte 1 + début acte 2 : rien
                { p: 0.000, value: 0 },
                { p: 0.450, value: 0 },

                // Acte 2 : montée en fin de hold (holdProgress 0.55 → 1.0)
                { p: 0.453, value: 0, ease: easeInOut },
                { p: 0.538, value: maxBloom, ease: easeInOut }, // pic à fin du hold acte 2

                // Transition 2 → 3 : on MAINTIENT le pic (éblouissement)
                { p: 0.600, value: maxBloom },

                // Acte 3 : descente progressive de l'éblouissement
                { p: 0.625, value: 0.1, ease: easeOut },            // stabilisation
                { p: 0.835, value: 0.0, ease: easeOut },            // stabilisation
                { p: 0.9, value: maxBloom * 0.3, ease: easeOut }, // fin transition entrante

                // Fin d'acte 3 : pulse final (optionnel, tu peux retirer)
                { p: 0.930, value: 0.3 },
                { p: 0.940, value: 3, ease: easeInOut },
                { p: 0.950, value: 0.5, ease: easeInOut },

                // Acte 4 : extinction
                { p: 0.960, value: 0, ease: easeOut },
                { p: 1.000, value: 0 },
            ]),

            luminanceSmoothing: createTimeline([
                { p: 0.000, value: 5 },
                { p: 0.490, value: 5 },
                { p: 0.538, value: isMobile ? 0 : 0, ease: easeInOut },
                { p: 0.627, value: 5, ease: easeInOut },
                { p: 1.000, value: 0 },
            ]),
        }
    }, [isMobile])

    useFrame(() => {
        if (!bloomRef.current) return
        const p = progress?.current ?? 0
        bloomRef.current.intensity = tl.intensity(p)
        bloomRef.current.luminanceSmoothing = tl.luminanceSmoothing(p)


    })

    return (
        <EffectComposer>
            <Bloom
                ref={bloomRef}
                intensity={0}
                luminanceThreshold={0}
                resolutionX={128}
                resolutionY={128}
            />
            {children}
        </EffectComposer>
    )
}